/**
 * CardsListV2 — Cards inside a single deck. Ported from the
 * prototype's `.screen-deckcards` (cms-redesign-mockup.html ~7703+),
 * with the `deck-context` strip + portrait card grid + unparented
 * modal pattern from styles 1545–1715.
 *
 * URL: /card-decks/:id/cards
 *
 * Layout (top to bottom):
 *   1. `.deck-context` — 100×56 deck thumb (module bg + header fg) +
 *      "Editing Cards · Card Deck" eyebrow + deck name + "← All decks"
 *      ghost button on the right. Anchors the user to which deck
 *      they're inside.
 *   2. Page-head — eyebrow with live stats ("N cards · M active · drag
 *      to reorder"), title "Cards in this deck", sub copy.
 *      Actions: "Unparented Cards (count)" → opens the unparented
 *      picker modal; "+ New Card" coral → /card-decks/:id/cards/create.
 *   3. `.cards-grid` — repeat(auto-fill, minmax(200px, 1fr)) of
 *      portrait `deck-card-item`s. Each has:
 *        – 5:7 art (front_image_url)
 *        – Status pin pill top-right (Active / Inactive)
 *        – Drag handle top-left, hover-revealed
 *        – Body: line-clamped title + #NN num (sort_order)
 *        – Actions row: Edit / Remove (Remove is the danger variant
 *          and detaches the card from the deck via
 *          /decks/:id/remove-card)
 *
 * Drag-to-reorder dispatches `sortCards` (PUT /decks/cards/sort-order)
 * with the same payload v1 already uses. Add/remove use the existing
 * `addCardToDeck` / `removeCardFromDeck` actions.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  fetchAllDecks,
  fetchOneDeck,
} from '../../../../actions/cardDeckActions';
import {
  fetchAllCards,
  fetchUnparentedCards,
  removeCardFromDeck,
  sortCards,
  addCardToDeck,
} from '../../../../actions/cardsActions';
import {
  Button,
  Modal,
  cn,
} from '../../../redesign';

/* ─── Icons ────────────────────────────────────────────────────────── */

const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);

const DragHandleIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="9"  cy="6"  r="0.5" />
    <circle cx="9"  cy="12" r="0.5" />
    <circle cx="9"  cy="18" r="0.5" />
    <circle cx="15" cy="6"  r="0.5" />
    <circle cx="15" cy="12" r="0.5" />
    <circle cx="15" cy="18" r="0.5" />
  </svg>
);

/* ─── Helpers ──────────────────────────────────────────────────────── */

function pad2(n) {
  const s = String(n);
  return s.length < 2 ? `0${s}` : s;
}

function resolveDeckColor(deck) {
  if (!deck?.primary_color_hex) return '#75A293';
  return deck.primary_color_hex.startsWith('#')
    ? deck.primary_color_hex
    : `#${deck.primary_color_hex}`;
}

/* ─── Deck context strip (top of the page) ─────────────────────────── */

function DeckContext({ deck }) {
  if (!deck) {
    return (
      <div
        className="bg-surface border-b border-sand text-text-muted"
        style={{ padding: '16px 28px', fontSize: 13 }}
      >
        Loading deck…
      </div>
    );
  }
  const deckColor = resolveDeckColor(deck);
  return (
    <div
      className="flex items-center bg-surface border-b border-sand"
      style={{ gap: 14, padding: '16px 28px' }}
    >
      <div
        className="bg-cream"
        style={{
          width: 100,
          height: 56,
          borderRadius: 8,
          backgroundImage: deck.module_image_url
            ? `url('${deck.module_image_url}')`
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          flexShrink: 0,
          position: 'relative',
          overflow: 'hidden',
          borderTop: `3px solid ${deckColor}`,
        }}
      >
        {deck.header_image_url && (
          <img
            src={deck.header_image_url}
            alt=""
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '50%',
              height: '50%',
              objectFit: 'contain',
            }}
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="font-condensed font-bold uppercase text-text-muted"
          style={{ fontSize: 10.5, letterSpacing: '0.08em' }}
        >
          Editing Cards · Card Deck
        </div>
        <div
          className="font-condensed font-bold text-navy truncate"
          style={{ fontSize: 18, letterSpacing: '-0.01em', lineHeight: 1.15, marginTop: 2 }}
          title={deck.name || ''}
        >
          {deck.name || 'Untitled deck'}
        </div>
      </div>
      <Link to="/card-decks" className="no-underline" style={{ marginLeft: 'auto' }}>
        <Button variant="ghost" leadingIcon={<span aria-hidden>←</span>}>
          All decks
        </Button>
      </Link>
    </div>
  );
}

/* ─── Single card tile ─────────────────────────────────────────────── */

function SortableCardItem({ card, position, deckId, onRemove }) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.id });

  const active = !!card.is_active;
  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 5 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={dragStyle}
      {...attributes}
      className="group bg-surface border border-sand overflow-hidden transition-all duration-200 relative hover:border-teal-green"
    >
      {/* Card art — 5:7 portrait. */}
      <div
        className="bg-cream"
        style={{
          aspectRatio: '5 / 7',
          backgroundImage: card.front_image_url
            ? `url('${card.front_image_url}')`
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          filter: active ? undefined : 'grayscale(100%)',
          opacity: active ? 1 : 0.65,
        }}
      >
        {/* Status pin pill — top-right ("Active" / "Inactive"). */}
        <span
          aria-hidden
          className="font-condensed font-bold uppercase text-white"
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            padding: '2px 8px',
            borderRadius: 999,
            background: active ? '#75A293' : '#9CA1A8',
            fontSize: 10,
            letterSpacing: '0.08em',
            boxShadow: '0 2px 6px #00000028',
          }}
        >
          {active ? 'Active' : 'Inactive'}
        </span>

        {/* Drag handle — top-left, hover-revealed. */}
        <span
          {...listeners}
          aria-label="Drag to reorder"
          tabIndex={0}
          className={cn(
            'grid place-items-center text-navy',
            'cursor-grab active:cursor-grabbing focus:outline-none',
            'opacity-0 group-hover:opacity-100',
          )}
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            width: 26,
            height: 26,
            background: '#FFFFFFCC',
            backdropFilter: 'blur(4px)',
            borderRadius: 4,
            transition: 'opacity 160ms ease',
            boxShadow: '0 2px 6px #00000028',
            touchAction: 'none',
          }}
        >
          <span style={{ width: 12, height: 12, display: 'inline-flex' }}>
            {DragHandleIcon}
          </span>
        </span>
      </div>

      {/* Body — title + #num. */}
      <div style={{ padding: '10px 12px 8px' }}>
        <div
          className={cn(
            'text-navy',
            !active && 'italic text-text-light',
          )}
          style={{
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: 34,
          }}
          title={card.title || ''}
        >
          {card.title || 'Untitled'}
        </div>
        <div
          className="font-mono text-text-muted"
          style={{ fontSize: 10.5, marginTop: 2 }}
        >
          #{pad2(position)}
        </div>
      </div>

      {/* Edit / Remove footer — always visible (prototype 1669–1672). */}
      <div className="flex" style={{ gap: 4, padding: '0 12px 10px' }}>
        <Link
          to={`/card-decks/${deckId}/cards/${card.id}/edit`}
          className="no-underline"
          style={{ flex: 1 }}
        >
          <button
            type="button"
            className="bg-surface border border-sand text-navy hover:bg-cream cursor-pointer w-full font-semibold"
            style={{
              height: 28,
              borderRadius: 8,
              fontSize: 11.5,
              transition: 'all 140ms ease',
              fontFamily: 'inherit',
            }}
          >
            Edit
          </button>
        </Link>
        <button
          type="button"
          onClick={() => onRemove(card)}
          className="border border-sand cursor-pointer font-semibold"
          style={{
            flex: 1,
            height: 28,
            borderRadius: 8,
            fontSize: 11.5,
            transition: 'all 140ms ease',
            fontFamily: 'inherit',
            background: '#FAF7F1',
            color: '#C4715B', /* coral-d */
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#E58A6A14';
            e.currentTarget.style.borderColor = '#E58A6A';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#FAF7F1';
            e.currentTarget.style.borderColor = '#E8E0CF';
          }}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

/* ─── Unparented picker modal ──────────────────────────────────────── */
/*
 *  Lists cards that exist but aren't assigned to any deck. Clicking
 *  "Add to deck" attaches the card to the current deck via
 *  PATCH /decks/:id/add-card. The card visually disappears from the
 *  modal once it's reparented (we filter against the deck's own
 *  active card ids on each render).
 */
function UnparentedCardsModal({ open, onClose, deckId, deckName }) {
  const dispatch = useDispatch();
  const unparented = useSelector((s) => s.cards.unparentedCards);
  const isLoading = useSelector((s) => s.cards.unparentedLoading);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return;
    dispatch(fetchUnparentedCards());
  }, [open, dispatch]);

  const handleAdd = (card) => {
    if (busyId) return;
    setBusyId(card.id);
    dispatch(
      addCardToDeck(deckId, card),
    );
    // The reducer pulls the card from `unparentedCards` on
    // ADD_CARD_TO_DECK_SUCCESS; reset busy after the action settles
    // (Swal's auto-close timer is the natural rhythm here).
    setTimeout(() => setBusyId(null), 1200);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow="Unparented Cards"
      title={`${Array.isArray(unparented) ? unparented.length : '—'} cards not assigned to any deck`}
      footer={
        <Button variant="ghost" onClick={onClose}>Done</Button>
      }
      // Wider than the default modal — cards-grid wants room.
      maxWidth={720}
    >
      <p
        className="text-text-light"
        style={{ marginBottom: 14, fontSize: 12, lineHeight: 1.5 }}
      >
        Cards that exist but aren't currently in a deck. Click{' '}
        <strong className="text-text font-semibold">Add to deck</strong> to pull
        one into{' '}
        <em>{deckName || 'this deck'}</em>, or open the card to reassign it
        elsewhere.
      </p>

      {isLoading || !Array.isArray(unparented) ? (
        <div className="text-text-muted text-center" style={{ padding: '24px 0', fontSize: 13 }}>
          Loading unparented cards…
        </div>
      ) : unparented.length === 0 ? (
        <div className="text-text-muted italic text-center" style={{ padding: '24px 0', fontSize: 13 }}>
          No unparented cards — every card lives in a deck.
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 14,
            gridAutoRows: 'max-content',
            // Cap the modal body height — the modal handles outer scroll.
            maxHeight: '60vh',
            overflow: 'auto',
          }}
        >
          {unparented.map((card) => {
            const active = !!card.is_active;
            const isBusy = busyId === card.id;
            return (
              <div
                key={card.id}
                className="bg-surface border border-sand overflow-hidden"
                style={{ borderRadius: 12, position: 'relative' }}
              >
                <div
                  className="bg-cream"
                  style={{
                    aspectRatio: '5 / 7',
                    backgroundImage: card.front_image_url
                      ? `url('${card.front_image_url}')`
                      : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                    filter: active ? undefined : 'grayscale(100%)',
                    opacity: active ? 1 : 0.65,
                  }}
                >
                  <span
                    aria-hidden
                    className="font-condensed font-bold uppercase text-white"
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: active ? '#75A293' : '#9CA1A8',
                      fontSize: 10,
                      letterSpacing: '0.08em',
                      boxShadow: '0 2px 6px #00000028',
                    }}
                  >
                    {active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div style={{ padding: '10px 12px 8px' }}>
                  <div
                    className="text-navy font-semibold"
                    style={{
                      fontSize: 13,
                      lineHeight: 1.3,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minHeight: 34,
                    }}
                  >
                    {card.title || 'Untitled'}
                  </div>
                  <div
                    className="font-mono text-text-muted"
                    style={{ fontSize: 10.5, marginTop: 2 }}
                  >
                    #{card.id}
                  </div>
                </div>
                <div style={{ padding: '0 12px 10px' }}>
                  <button
                    type="button"
                    onClick={() => handleAdd(card)}
                    disabled={isBusy}
                    className="inline-flex items-center justify-center w-full font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-wait"
                    style={{
                      height: 28,
                      borderRadius: 8,
                      gap: 4,
                      fontSize: 11.5,
                      background: '#75A293',
                      color: '#FFFFFF',
                      border: '1px solid #75A293',
                      transition: 'all 140ms ease',
                      fontFamily: 'inherit',
                    }}
                    onMouseEnter={(e) => {
                      if (isBusy) return;
                      e.currentTarget.style.background = '#5C7E72';
                      e.currentTarget.style.borderColor = '#5C7E72';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#75A293';
                      e.currentTarget.style.borderColor = '#75A293';
                    }}
                  >
                    <span aria-hidden style={{ fontSize: 14, lineHeight: 1, fontWeight: 700 }}>+</span>
                    Add to deck
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function CardsListV2() {
  const dispatch = useDispatch();
  const { id: deckIdParam } = useParams();
  const deckId = Number(deckIdParam);

  // The list endpoint may not yet have been fetched (e.g. deep-link
  // straight into /card-decks/:id/cards), so we also dispatch
  // fetchOneDeck to populate the context strip.
  const decksList = useSelector((s) => s.card_decks.decks);
  const oneDeck = useSelector((s) => s.card_decks.deck?.data);
  const cards = useSelector((s) => s.cards.cards);

  const deck = useMemo(() => {
    if (Array.isArray(decksList)) {
      const fromList = decksList.find((d) => d.id === deckId);
      if (fromList) return fromList;
    }
    if (oneDeck && oneDeck.id === deckId) return oneDeck;
    return null;
  }, [decksList, oneDeck, deckId]);

  const [localList, setLocalList] = useState([]);
  const [unparentedOpen, setUnparentedOpen] = useState(false);

  // Always re-fetch the deck's cards + the deck record itself on
  // mount so optimistic mutations don't drift.
  useEffect(() => {
    dispatch(fetchAllCards(deckId));
    dispatch(fetchOneDeck(deckId));
  }, [deckId, dispatch]);

  // Lazy-load the full deck list only if the user landed here via
  // deep link (so DeckContext can resolve the deck from cache). The
  // empty check is read-only and the effect deliberately doesn't
  // depend on `decksList` — re-running on every list mutation would
  // spam the endpoint.
  const decksListLoaded = Array.isArray(decksList);
  useEffect(() => {
    if (!decksListLoaded) {
      dispatch(fetchAllDecks());
    }
  }, [decksListLoaded, dispatch]);

  useEffect(() => {
    if (!Array.isArray(cards)) return;
    const sorted = [...cards].sort(
      (a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0),
    );
    setLocalList(sorted);
  }, [cards]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = localList.findIndex((c) => c.id === active.id);
      const newIndex = localList.findIndex((c) => c.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;

      const next = arrayMove(localList, oldIndex, newIndex);
      setLocalList(next);
      dispatch(sortCards(next));
    },
    [localList, dispatch],
  );

  const handleRemove = useCallback(
    (card) => {
      Swal.fire({
        title: 'Remove this card from the deck?',
        text: `"${card.title || 'Untitled'}" will be detached. The card itself isn't deleted — you can re-add it from Unparented Cards.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove',
        cancelButtonText: 'Cancel',
      }).then((result) => {
        if (result.value) {
          dispatch(removeCardFromDeck(deckId, card.id));
        }
      });
    },
    [deckId, dispatch],
  );

  const ids = useMemo(() => localList.map((c) => c.id), [localList]);
  const activeCount = useMemo(
    () => localList.filter((c) => c.is_active).length,
    [localList],
  );

  return (
    <div className="bg-bg min-h-full">
      <DeckContext deck={deck} />

      <div style={{ padding: '20px 28px 0' }}>
        <div className="flex items-start" style={{ gap: 16, flexWrap: 'wrap' }}>
          <div className="flex-1 min-w-0">
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 11, letterSpacing: '0.08em' }}
            >
              {localList.length} cards · {activeCount} active · drag any card to reorder
            </div>
            <h1
              className="text-navy"
              style={{
                fontSize: 28,
                fontWeight: 700,
                margin: '4px 0 6px',
                letterSpacing: '-0.015em',
                lineHeight: 1.15,
              }}
            >
              Cards in this deck
            </h1>
            <div
              className="text-text-light"
              style={{ fontSize: 13.5, lineHeight: 1.5 }}
            >
              Tap to view the front, drag to reorder, or pull from the
              unparented pool to add existing cards back to this deck.
            </div>
          </div>
          <div className="flex items-center" style={{ gap: 8 }}>
            <Button
              variant="default"
              onClick={() => setUnparentedOpen(true)}
            >
              Unparented Cards
            </Button>
            <Link to={`/card-decks/${deckId}/cards/create`} className="no-underline">
              <Button variant="coral" leadingIcon={PlusSign}>
                New Card
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div style={{ padding: '22px 28px 32px' }}>
        {!Array.isArray(cards) ? (
          <div
            className="flex items-center justify-center text-text-muted"
            style={{ height: 256 }}
          >
            Loading cards…
          </div>
        ) : localList.length === 0 ? (
          <div
            className="bg-surface border border-sand text-text-muted italic text-center"
            style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
          >
            No cards in this deck yet — add one with the "+ New Card" button or
            pull one from the unparented pool.
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={ids} strategy={rectSortingStrategy}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 18,
                  gridAutoRows: 'max-content',
                  alignContent: 'start',
                }}
              >
                {localList.map((c, idx) => (
                  <SortableCardItem
                    key={c.id}
                    card={c}
                    position={idx + 1}
                    deckId={deckId}
                    onRemove={handleRemove}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <UnparentedCardsModal
        open={unparentedOpen}
        onClose={() => setUnparentedOpen(false)}
        deckId={deckId}
        deckName={deck?.name}
      />
    </div>
  );
}
